import { useToast } from "@chakra-ui/react";
import { createContext, ReactNode, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";

import { UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { $ok } from "foundation-ts/commons";
import { TSError } from "foundation-ts/tserrors";
import { Nullable, UUID } from "foundation-ts/types";
import { $inspect } from "foundation-ts/utils";

import { queryClient } from "@/main";
import { UserProfileID } from "@/hooks/hooksConstants";
import { getUserUuid, isLoggedIn } from "@services/authentication.service";
import { getAdminProfile, getUserProfile } from "@services/users.service";
import { optlog } from "@utils/functions";
import { defaultWarningToast } from "@utils/toast";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [authMode, setAuthMode]                         = useState<Nullable<AuthMode>>(null);
    const [authState, setAuthState]                       = useState<AuthState>(AuthState.UNAUTHENTICATED);
    const [pendingAdminId, setPendingAdminId]             = useState<Nullable<UUID>>(null);
    const [isInitializing, setIsInitializing]             = useState(true);
    const [isAuthenticatedByOTP, setIsAuthenticatedByOTP] = useState(false);
    const [pollingState, setPollingState]                 = useState<PollingState>(PollingState.IDLE);
    const pollAttemptsRef = useRef(0);
    const toast = useToast();

    const {
        data: connectedUser = null,
        isLoading: isRestoringSession,
        isRefetching: isRefetchingUser,
    } = useQuery<Nullable<UserDto>>({
        queryKey: [UserProfileID, authMode],
        queryFn: async () => {
            const savedMode = localStorage.getItem("mode") as AuthMode | null;
            const effectiveMode = authMode ?? savedMode;

            if (!isLoggedIn() || !effectiveMode) return null;

            const userId = getUserUuid();
            if (!userId) return null;

            const user = effectiveMode === AuthMode.ADMIN
                ? await getAdminProfile(userId)
                : await getUserProfile(userId);

            if (pollingState === PollingState.RUNNING) {
                pollAttemptsRef.current += 1;

                const corporation = user?.corporations?.first();
                const shouldStop = 
                    corporation?.mandate?.failedToChange ||
                    corporation?.mandate?.status === "valid" ||
                    corporation?.mandate?.status === "invalid";
                if (shouldStop) {
                    setPollingState(PollingState.STOPPED);
                } else if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
                    setPollingState(PollingState.EXHAUSTED);
                }
            }

            if (!authMode && user) {
                setAuthMode(effectiveMode);
                setAuthState(AuthState.AUTHENTICATED);
            }

            return user;
        },
        enabled: isLoggedIn(),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchInterval: pollingState === PollingState.RUNNING ? 2000 : false,
        refetchIntervalInBackground: false,
        retry: false,
        onError: (e: any) => {
            if ((e instanceof TSError) && e.errorCode === 401) {
                toast(defaultWarningToast("Suite à l'expiration de votre session, vous avez été redirigé vers la page d'authentification."));
                disconnect();
            } else {
                optlog($inspect(e));
            }
        }
    });

    const setStandardUser = (user: UserDto) => {
        setAuthMode($ok(user) ? AuthMode.USER : null);
        setAuthState($ok(user) ? AuthState.AUTHENTICATED : AuthState.UNAUTHENTICATED);
        setPendingAdminId(null);
        localStorage.setItem("mode", AuthMode.USER);
        queryClient.setQueryData([UserProfileID, AuthMode.USER], user);
    }

    const setPendingAdmin = (adminId: UUID) => {
        setAuthMode(null);
        setAuthState(AuthState.PENDING_OTP);
        setPendingAdminId(adminId);
    };

    const setAdminUser = (user: UserDto) => {
        setAuthMode(AuthMode.ADMIN);
        setAuthState(AuthState.AUTHENTICATED);
        setPendingAdminId(null);
        localStorage.setItem("mode", AuthMode.ADMIN);
        queryClient.setQueryData([UserProfileID, AuthMode.ADMIN], user);
    };

    const setAuthenticatedByOTP = (b: boolean) => {
        setIsAuthenticatedByOTP(b);
    }
    const disconnect = (mode?: AuthMode) => {
        if ($ok(mode) && authMode !== mode) { return; }

        setAuthMode(null);
        setAuthState(AuthState.UNAUTHENTICATED);
        setPendingAdminId(null);
        setIsAuthenticatedByOTP(false);
        setPollingState(PollingState.IDLE);
        localStorage.removeItem("mode");
        queryClient.removeQueries([UserProfileID]);
    }

    const startPollingUser = () => {
        pollAttemptsRef.current = 0;
        setPollingState(PollingState.RUNNING);
    }

    const stopPollingUser = () => {
        pollAttemptsRef.current = 0;
        setPollingState(PollingState.STOPPED);
    }

    const value: AuthContextType = {
        connectedUser,
        connectedProfileId: connectedUser?.apid ?? null,
        pendingAdminId,
        authMode,
        authState,
        isInitializing,
        isAuthenticated: authState === AuthState.AUTHENTICATED,
        isUserAuthenticated: authState === AuthState.AUTHENTICATED && authMode === AuthMode.USER,
        isAdminAuthenticated: authState === AuthState.AUTHENTICATED && authMode === AuthMode.ADMIN,
        isPendingOTP: authState === AuthState.PENDING_OTP,
        isAuthenticatedByOTP,
        isRefetchingUser,
        pollingState,
        setStandardUser,
        setPendingAdmin,
        setAdminUser,
        setAuthenticatedByOTP,
        disconnect,
        startPollingUser,
        stopPollingUser,
    }

    useEffect(() => {
        if (!isRestoringSession) {
            setIsInitializing(false);
        }
    }, [isRestoringSession]);

    useEffect(() => {
        queryClient.setDefaultOptions({
            queries: {
                refetchOnWindowFocus: false,
                retry: 1,
                onError: (e: any) => {
                    if ((e instanceof TSError) && (e.errorCode === 401)) {
                        toast(defaultWarningToast("Suite à l'expiration de votre session, vous avez été redirigé vers la page d'authentification."));
                        disconnect();
                    } else {
                        optlog($inspect(e));
                    }
                }
            },
        });
    }, [toast]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export interface AuthContextType {
    connectedUser: Nullable<UserDto>;
    connectedProfileId: Nullable<UUID>;
    pendingAdminId: Nullable<UUID>;
    authMode: Nullable<AuthMode>;
    authState: AuthState;
    isInitializing: boolean;
    isAuthenticated: boolean;
    isUserAuthenticated: boolean;
    isAdminAuthenticated: boolean;
    isPendingOTP: boolean;
    isAuthenticatedByOTP: boolean;
    isRefetchingUser: boolean;
    pollingState: PollingState;
    setStandardUser: (user: UserDto) => void;
    setPendingAdmin: (uid: UUID) => void;
    setAdminUser: (user: UserDto) => void;
    setAuthenticatedByOTP: (b: boolean) => void;
    disconnect: (mode?: AuthMode) => void;
    startPollingUser: () => void;
    stopPollingUser: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export enum AuthMode {
    USER = "USER",
    ADMIN = "ADMIN"
}

export enum PollingState {
    IDLE = 'IDLE',
    RUNNING = 'RUNNING',
    EXHAUSTED = 'EXHAUSTED',
    STOPPED = 'STOPPED',
}

enum AuthState {
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  PENDING_OTP = 'PENDING_OTP',
  AUTHENTICATED = 'AUTHENTICATED',
}

const MAX_POLL_ATTEMPTS = 5;
